import './DatasetDetail.css';

import { FC, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { IonCard } from '@ionic/react';
import Detail from '../../components/Manager/Detail/Detail';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import useRoutes from '../../hooks/useRoutes';
import { useSelectedRowsContext } from '../../hooks/context/useSelectedRowsContext';
import { getDataKey } from '../../utils/getDataKey';
import { useExternalUrlDetailGuard } from './hooks/useExternaUrlDetailGuard';
import { useDatasetUrlDetailReloadGuard } from './hooks/useDatasetUrlDetailReloadGuard';
import { useFormReturnDetailFetchGuard } from './hooks/useFormReturnDetailFetchGuard';
import SeptimLoadingSpinner from '../../components/Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import { GlobalLocationStateType } from '../../types/types';
import { getDatasetDataApiRequestData } from '../../api/utils/requestData/getDatasetDataApiRequestData';
import RequestDataProvider from '../../context/RequestDataContext';
import { useUserState } from '../../hooks/context/useUserState';

const DatasetDetail: FC = () => {
  const history = useHistory();
  const routes = useRoutes();
  const { pathname } = useLocation<GlobalLocationStateType>();
  const { datasetId } = useParams<{
    datasetId: string;
    itemId: string;
  }>();
  const {
    dataset,
    datasetData,
    data,
    locallyRemovedRowsCount,
    slaveTabIndex,
    slaveDatasets,
    slaveLazyStates,
    slaveFilters,
    referenceDatasets,
    activeFilters,
    activeSorting,
    datasetControl,
    setSlaveTabIndex,
    loadMoreData,
    setSlaveDatasets,
    setSlaveLazyStates,
    setSlaveFilters,
    fetchDatasetData,
    setReferenceDatasets,
  } = useDatasetContext();
  const {
    state: { token: sessionToken },
  } = useUserState();
  const { expandedRow, selectedRows, setExpandedRow } =
    useSelectedRowsContext();
  const { isLoading: isExternalUrlProcessing, externalRow } =
    useExternalUrlDetailGuard();
  const { isLoading: isFormReturnProcessing } = useFormReturnDetailFetchGuard();
  useDatasetUrlDetailReloadGuard();

  const dataKey = getDataKey(dataset);
  const totalRowsCount = Math.max(
    0,
    (datasetData?.totalCount || 0) - locallyRemovedRowsCount
  );
  const nameColumn = useMemo(
    () => dataset?.items?.find((column) => column.visible !== false),
    [dataset]
  );
  const detailColumns = useMemo(
    () =>
      dataset?.items?.filter((column) =>
        dataset.idItem
          ? column.detailVisible !== false
          : column.visible !== false
      ),
    [dataset]
  );
  const rows = useMemo(() => {
    if (selectedRows.length) {
      return selectedRows;
    }

    if (externalRow) {
      return [externalRow];
    }
    return data ?? [];
  }, [selectedRows, data, externalRow]);

  // Scope request data for template resolving in descendants (dataset label
  // placeholders) — mirrors the DatasetList provider.
  const requestData = useMemo(() => {
    if (!dataset) return undefined;

    return getDatasetDataApiRequestData(
      dataset,
      activeSorting,
      sessionToken,
      0,
      activeFilters || undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      datasetControl ?? undefined
    );
  }, [dataset, activeSorting, activeFilters, datasetControl, sessionToken]);

  const handleOpenFilter = () => {
    history.push(routes.datasetDetailMasterFilter(datasetId), {
      fromPathname: pathname,
    });
  };

  if (isExternalUrlProcessing || isFormReturnProcessing) {
    return (
      <div className="page-container dataset-detail-loading">
        <IonCard>
          <SeptimLoadingSpinner />
        </IonCard>
      </div>
    );
  }

  return (
    <div className="page-container">
      <IonCard>
        <RequestDataProvider requestData={requestData}>
          <Detail
            isExternalRowDisplay={externalRow !== undefined}
            rows={rows}
            datasetData={datasetData}
            dataset={dataset}
            showSelectedRows={selectedRows.length ? true : false}
            dataKey={dataKey}
            nameColumn={nameColumn}
            detailColumns={detailColumns}
            slaveTabIndex={slaveTabIndex}
            expandedRow={expandedRow}
            totalRowsCount={totalRowsCount}
            slaveDatasets={slaveDatasets}
            slaveLazyStates={slaveLazyStates}
            slaveFilters={slaveFilters}
            referenceDatasets={referenceDatasets}
            openFilter={handleOpenFilter}
            refetchData={fetchDatasetData}
            setSlaveTabIndex={setSlaveTabIndex}
            loadMoreData={loadMoreData}
            setExpandedRow={setExpandedRow}
            setSlaveDatasets={setSlaveDatasets}
            setSlaveLazyStates={setSlaveLazyStates}
            setSlaveFilters={setSlaveFilters}
            setReferenceDatasets={setReferenceDatasets}
            openFullscreenReference={() => {}}
          />
        </RequestDataProvider>
      </IonCard>
    </div>
  );
};

export default DatasetDetail;
